import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { CHART_TYPES, ChartTypeSwitch, HorizontalBars, PieWithLegend, VerticalBarChart, resolveChartType, useElementWidth } from './ResultsCharts';
import { companyLogoUrl } from '../utils/companyLogo';
import { useParticipantTheme } from '../utils/theme';
import WordCloud from './WordCloud';
import SvodxLogo from './brand/SvodxLogo';
import HeatmapView from './heatmap/HeatmapView';
import ShareCard from './presentations/ShareCard';
import { isJustVoted, shareUrl } from './presentations/shareUtils';
import './PollResults.css';

// Для этих типов вопросов вид диаграммы фиксирован
/** Свои ответы из варианта «Другое»: целые фразы, одинаковые объединены, частые — крупнее. */
function OtherPhrases({ data, label, compact }) {
  const phrases = (data.phrases || []).slice(0, compact ? 20 : 60);
  if (!phrases.length && !data.pending) return null;
  return (
    <div className={`other-phrases${compact ? ' other-phrases--compact' : ''}`}>
      <div className="other-phrases__head">
        <span>Свои ответы в «{label}»</span>
        {data.approved > 0 && <b>{data.approved}</b>}
      </div>
      {phrases.length > 0 ? (
        <WordCloud items={phrases} size="compact" />
      ) : (
        <p className="other-phrases__empty">Ответы появятся здесь после автопроверки.</p>
      )}
      {data.pending > 0 && phrases.length > 0 && (
        <p className="other-phrases__pending">Ещё {data.pending} на автопроверке — появятся через несколько секунд.</p>
      )}
    </div>
  );
}

const NON_OPTION_TYPES = ['ranking', 'number', 'rating', 'wordcloud', 'grid', 'heatmap'];
// Картинку эти типы рисуют сами: сетку или тепловую карту поверх неё
const OWN_IMAGE_TYPES = ['grid', 'heatmap'];
const CHART_PARAM_VALUES = ['bar', 'horizontal', 'pie'];

const API_URL = process.env.REACT_APP_API_URL || '';

// embed — встраиваемый вид для iframe (сайт, LMS, вебинарная комната): без шапки сервиса,
// параметры: q — номер вопроса, qr=1 — QR для голосования, header=0 — скрыть заголовок.
// Во встраиваемом виде всё помещается в окно: один вопрос за раз (без q — с переключением),
// график занимает оставшуюся высоту.
function PollResults({ embed = false }) {
  const { pollId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const embedQuestion = embed ? parseInt(searchParams.get('q'), 10) || null : null;
  const embedShowQr = embed && searchParams.get('qr') === '1';
  const embedShowHeader = !embed || searchParams.get('header') !== '0';
  const embedChart = embed && CHART_PARAM_VALUES.includes(searchParams.get('chart')) ? searchParams.get('chart') : null;
  const [embedLocked, setEmbedLocked] = useState(null);
  const [setRootEl, rootWidth] = useElementWidth();
  const [embedIndex, setEmbedIndex] = useState(0);
  const [fitEl, setFitEl] = useState(null);
  const [fitHeight, setFitHeight] = useState(0);
  // Высота окна и шапки: на компьютере и проекторе каждый вопрос с графиком помещается в один экран
  const [viewHeight, setViewHeight] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 900));
  const [headerEl, setHeaderEl] = useState(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [stats, setStats] = useState(null);
  // «Поделиться презентацией»: приходит только в ответе /stats, обновления по веб-сокету его не содержат
  const [share, setShare] = useState(null);
  useParticipantTheme(stats?.theme);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollActive, setPollActive] = useState(true);
  const [chartTypes, setChartTypes] = useState({});

  const changeChartType = (questionId, chartType) => {
    setChartTypes(prev => ({
      ...prev,
      [questionId]: chartType
    }));
  };

  // Тип диаграммы: выбранный зрителем → из кода встраивания (chart=) → автоматический по числу и длине вариантов
  const getChartType = (question) => resolveChartType(question, chartTypes[question.id] || embedChart, chartAreaWidth);

  const fetchStats = useCallback(async () => {
    try {
      if (/^\d+$/.test(String(pollId))) {
        const legacyPollRes = await fetch(`${API_URL}/polls/${pollId}`);
        if (legacyPollRes.ok) {
          const legacyPoll = await legacyPollRes.json();
          if (legacyPoll?.public_id && legacyPoll.public_id !== pollId) {
            navigate(`/${embed ? 'embed' : 'poll'}/${legacyPoll.public_id}${location.search}`, { replace: true });
            return;
          }
        }
      }

      if (embed) {
        const accessRes = await fetch(`${API_URL}/public/polls/${pollId}/embed`);
        if (!accessRes.ok) {
          throw new Error('Опрос не найден');
        }
        const access = await accessRes.json();
        if (!access.allowed) {
          setEmbedLocked(access.message || 'Встраивание недоступно на тарифе организатора');
          return;
        }
      }

      const response = await fetch(`${API_URL}/public/polls/${pollId}/stats`);
      if (!response.ok) {
        throw new Error('Опрос не найден');
      }
      const data = await response.json();
      setStats(data);
      setShare(data.share || null);
      setError(null);

      // Определяем активность опроса из API
      setPollActive(data && data.is_active === true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pollId, navigate, embed, location.search]);

  // Высота области графика во встраиваемом виде — график подстраивается под окно iframe
  useEffect(() => {
    if (!fitEl) return undefined;
    const update = () => setFitHeight(fitEl.clientHeight);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(fitEl);
    return () => observer.disconnect();
  }, [fitEl]);

  useEffect(() => {
    const update = () => setViewHeight(window.innerHeight);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!headerEl) return undefined;
    const update = () => setHeaderHeight(headerEl.offsetHeight);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, [headerEl]);

  useEffect(() => {
    if (!embed) return undefined;
    document.body.classList.add('is-embed');
    return () => document.body.classList.remove('is-embed');
  }, [embed]);

  useEffect(() => {
    if (embedLocked) return undefined;
    fetchStats();

    let socket = null;
    let reconnectTimeout = null;
    let reconnectAttempts = 0;
    const maxReconnectDelay = 30000; // 30 seconds

    const connect = () => {
      // Самый надежный способ: относительный путь от корня. 
      // Браузер сам подставит нужный домен и протокол (ws/wss).
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const fullWsUrl = `${protocol}//${window.location.host}/ws/public/polls/${pollId}`;
      
      console.log(`Connecting to WebSocket: ${fullWsUrl} (attempt ${reconnectAttempts + 1})`);
      socket = new WebSocket(fullWsUrl);

      socket.onopen = () => {
        console.log('WebSocket connected successfully');
        reconnectAttempts = 0; // Reset attempts on successful connection
      };

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('WebSocket message received:', message.type);

          if (message.type === 'initial_stats' || message.type === 'stats_updated') {
            setStats(message.data);
          }
          if (message.type === 'poll_status_changed') {
            setPollActive(message.is_active);
          }
        } catch (e) {
          console.error('Error parsing WS message:', e);
        }
      };

      socket.onerror = (error) => {
        console.error('WebSocket error occurred:', error);
      };

      socket.onclose = (event) => {
        const wasClean = event.wasClean;
        console.log(`WebSocket closed (code: ${event.code}, clean: ${wasClean}). Reconnecting...`);
        socket = null;
        
        // Нарастающая пауза со случайным разбросом: после перезапуска сервера экраны
        // переподключаются не одновременно. Код 1013 — сервер перегружен, ждём дольше.
        const base = Math.min(1000 * Math.pow(2, reconnectAttempts), maxReconnectDelay);
        const delay = (event.code === 1013 ? maxReconnectDelay : base) * (0.5 + Math.random());
        reconnectAttempts++;
        
        reconnectTimeout = setTimeout(() => {
          connect();
        }, delay);
      };
    };

    connect();

    return () => {
      console.log('Cleaning up WebSocket connection');
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (socket) {
        socket.onclose = null; // Prevent reconnect on intentional close
        socket.close();
      }
    };
  }, [pollId, fetchStats, embedLocked]);

  const containerClass = `results-container${embed ? ' results-container--embed' : ''}`;

  if (embedLocked) {
    return (
      <div className={containerClass}>
        <div className="error-card embed-locked">
          <h2>Встраивание недоступно</h2>
          <p>{embedLocked}</p>
          <a href="https://svodx.ru" target="_blank" rel="noopener noreferrer">svodx.ru</a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={containerClass}>
        <div className="loading">
          <div className="spinner"></div>
          <p>Загрузка результатов...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={containerClass}>
        <div className="error-card">
          <h2>Ошибка</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className={containerClass}>
        <div className="error-card">
          <h2>Опрос не найден</h2>
        </div>
      </div>
    );
  }

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  const compactCharts = isMobile || embed;
  // Место справа под плавающий QR-код (на узком компьютере QR меньше)
  const qrReserve = rootWidth >= 1200 ? 230 : 170;
  // Ширина области графика
  const chartAreaWidth = embed || isMobile ? rootWidth - 32 : rootWidth - qrReserve - 64;
  // Крупнее подписи на большом экране и проекторе
  const fontScale = compactCharts ? 1 : rootWidth >= 1700 ? 1.35 : rootWidth >= 1300 ? 1.15 : 1;
  // Высота графика на компьютере: вопрос целиком в окне (заголовок, переключатель, график)
  const screenChartHeight = Math.round(Math.min(680, Math.max(300, viewHeight - (headerHeight || 90) - 230)));
  // Картинку к вопросу ставим рядом с графиком, если хватает ширины
  const imageBeside = !compactCharts && chartAreaWidth >= 760;
  const voteUrl = `${window.location.origin}/vote/${stats.poll_public_id || pollId}`;
  const questionCount = stats.questions.length;
  const currentIndex = embedQuestion ? embedQuestion - 1 : Math.min(embedIndex, Math.max(questionCount - 1, 0));
  const shownQuestions = stats.questions
    .map((question, index) => ({ question, index }))
    .filter(({ index }) => !embed || index === currentIndex);
  const showEmbedNav = embed && !embedQuestion && questionCount > 1;
  // Во встраиваемом виде высота графика — всё свободное место в окне
  const fitChartHeight = Math.max(fitHeight - 8, 120);
  const barHeight = embed ? fitChartHeight : (isMobile ? 340 : screenChartHeight);
  const distributionHeight = embed ? Math.max(fitHeight - 130, 120) : (compactCharts ? 300 : Math.max(240, screenChartHeight - 130));

  return (
    <div
      className={containerClass}
      ref={setRootEl}
      style={embed ? undefined : { '--results-header-h': `${headerHeight || 90}px`, '--qr-reserve': `${qrReserve}px` }}
    >
      {embed ? (
        (embedShowHeader || (embedShowQr && pollActive)) && (
          <div className="embed-top">
            {embedShowHeader && stats.company_logo_path && (
              <img className="embed-logo" src={companyLogoUrl(stats.company_logo_path)} alt="Логотип" />
            )}
            {embedShowHeader && (
              <div className="embed-header">
                <h1>{stats.title}</h1>
                <div className="embed-header__meta">
                  <span>Ответов: <strong>{stats.total_responses}</strong></span>
                  <span className={`poll-status ${pollActive ? 'active' : 'inactive'}`}>
                    {pollActive ? 'Идёт голосование' : 'Голосование завершено'}
                  </span>
                </div>
              </div>
            )}
            {embedShowQr && pollActive && (
              <div className="embed-vote">
                <QRCodeSVG value={voteUrl} size={72} level="M" includeMargin={true} />
                <div>
                  <p className="embed-vote__title">Голосуйте с телефона</p>
                  <a href={voteUrl} target="_blank" rel="noopener noreferrer">{voteUrl.replace(/^https?:\/\//, '')}</a>
                </div>
              </div>
            )}
          </div>
        )
      ) : (<>
      <header className="results-header" ref={setHeaderEl}>
        <div className="results-header__inner">
          {/* Корпоративный тариф: только логотип организации; иначе — логотип SVODx */}
          {stats.company_logo_path ? (
            <img className="results-brand__logo" src={companyLogoUrl(stats.company_logo_path)} alt="Логотип организации" />
          ) : (
            <a className="results-brand" href="https://svodx.ru" target="_blank" rel="noopener noreferrer" title="Сервис Визуализации Опросов и Данных">
              <SvodxLogo />
            </a>
          )}
          <div className="results-header__main">
            <h1>{stats.title}</h1>
            {stats.description && <p className="description">{stats.description}</p>}
          </div>
          <div className="results-header__meta">
            <span className="total-responses">Ответов: <strong>{stats.total_responses}</strong></span>
            <span className={`poll-status ${pollActive ? 'active' : 'inactive'}`}>
              {pollActive ? 'Идёт голосование' : 'Голосование завершено'}
            </span>
          </div>
        </div>
      </header>
      {/* Только тому, кто сейчас ответил: проектор и остальные зрители этой карточки не видят */}
      {share?.enabled && isJustVoted(stats.poll_public_id || pollId) && (
        <div className="results-share">
          <ShareCard kind="poll" refId={stats.poll_public_id || pollId} share={share} dismissible />
        </div>
      )}
      </>)}

      <div className="questions-container">
        {embedQuestion && shownQuestions.length === 0 && (
          <div className="no-responses">
            <p>Вопрос №{embedQuestion} не найден в опросе.</p>
          </div>
        )}
        {shownQuestions.map(({ question, index }) => {
          const hasChartChoice = !NON_OPTION_TYPES.includes(question.type);
          const chartType = hasChartChoice ? getChartType(question) : null;
          // На компьютере вопрос должен влезть в экран: картинка над графиком и блок «Другое» забирают часть высоты
          const imageAbove = !embed && !compactCharts && question.image_id && !OWN_IMAGE_TYPES.includes(question.type) && !imageBeside;
          const imageHeight = imageAbove ? Math.round(Math.min(260, barHeight * 0.4)) : 0;
          const otherReserve = !embed && !compactCharts && question.other_answers ? 170 : 0;
          const qChartHeight = compactCharts ? barHeight : Math.max(220, barHeight - imageHeight - otherReserve - (imageAbove ? 16 : 0));
          return (
          <div key={question.id} className="question-card">
            <div className="question-header">
              <h2 className="question-title">
                Вопрос {index + 1} из {stats.questions.length}: {question.text}
                {question.allow_multiple && <span className="multiple-choice-badge"> (Множественный выбор)</span>}
              </h2>
              {embed ? (
                hasChartChoice && (
                  <ChartTypeSwitch
                    value={chartType}
                    onChange={(type) => changeChartType(question.id, type)}
                    allowPie={!question.allow_multiple}
                  />
                )
              ) : (
                <div className="chart-type-selector">
                  {question.type === 'number' && <span className="ranking-badge">Ответ числом</span>}
                  {question.type === 'rating' && <span className="ranking-badge">Оценка (NPS)</span>}
                  {question.type === 'wordcloud' && <span className="ranking-badge">Облако слов</span>}
                  {question.type === 'heatmap' && <span className="ranking-badge">Тепловая карта</span>}
                  {question.type === 'ranking' && <span className="ranking-badge">Рейтинг (баллы)</span>}
                  {hasChartChoice && (
                    <div className="seg-switch" role="radiogroup" aria-label="Тип диаграммы">
                      {CHART_TYPES.filter((t) => !question.allow_multiple || t.value !== 'pie').map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          role="radio"
                          aria-checked={chartType === t.value}
                          className={chartType === t.value ? 'is-active' : ''}
                          onClick={() => changeChartType(question.id, t.value)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={`question-body${question.image_id && !OWN_IMAGE_TYPES.includes(question.type) && imageBeside ? ' question-body--beside' : ''}`}>
            {question.image_id && !OWN_IMAGE_TYPES.includes(question.type) && (
              <div className="question-image-container">
                <img
                  src={`/proxy/image/${question.image_id}`}
                  alt="Картинка к вопросу"
                  style={{ maxHeight: embed ? undefined : (imageBeside ? `${barHeight}px` : `${compactCharts ? 260 : imageHeight}px`) }}
                />
              </div>
            )}

            <div
              className={`chart-container chart-container--${chartType || question.type}`}
              ref={embed ? setFitEl : undefined}
            >
              {/* Числовой или рейтинговый вопрос: среднее и распределение */}
              {(question.type === 'number' || question.type === 'rating') && (
                <div className="number-stats">
                  {question.numeric_stats && question.numeric_stats.count > 0 ? (
                    <>
                      <div className="number-summary">
                        {question.type === 'rating' ? (
                          <>
                            <div className="rating-summary-stars" aria-hidden>
                              {(() => {
                                const scale = question.max_value || 5;
                                const filled = Math.min(Math.round(question.numeric_stats.average || 0), scale);
                                return Array.from({ length: scale }).map((_, i) => (
                                  <span key={i}>{i < filled ? '★' : '☆'}</span>
                                ));
                              })()}
                            </div>
                            <div className="number-summary__value">
                              {question.numeric_stats.average != null ? question.numeric_stats.average.toFixed(2) : '—'}
                              <span> / {question.max_value || 5}</span>
                            </div>
                          </>
                        ) : (
                          <div className="number-summary__value">
                            <small>Среднее</small> {question.numeric_stats.average != null ? question.numeric_stats.average.toFixed(2) : '—'}
                          </div>
                        )}
                        <div className="number-summary__meta">
                          Мин: {question.numeric_stats.min != null ? question.numeric_stats.min : '—'} ·
                          Макс: {question.numeric_stats.max != null ? question.numeric_stats.max : '—'} ·
                          Ответов: {question.numeric_stats.count}
                        </div>
                      </div>
                      {question.type === 'number' && question.numeric_stats.distribution && question.numeric_stats.distribution.length > 0 && (
                        <VerticalBarChart
                          data={question.numeric_stats.distribution}
                          dataKey="bin_label"
                          height={distributionHeight}
                          showPercent={false}
                          valueLabel="Количество"
                          numericAxis
                          singleColor
                          fontScale={fontScale}
                        />
                      )}
                    </>
                  ) : (
                    <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '20px' }}>Нет ответов</p>
                  )}
                </div>
              )}

              {question.type === 'wordcloud' && (
                <div className="wordcloud-block">
                  <div className="wordcloud-block__meta">
                    Одобрено: <strong>{question.approved_count || 0}</strong> ·
                    На модерации: <strong>{question.pending_count || 0}</strong> ·
                    Отклонено: <strong>{question.rejected_count || 0}</strong>
                  </div>
                  {question.wordcloud && question.wordcloud.length > 0 ? (
                    <WordCloud
                      items={question.wordcloud.map((w) => ({ text: w.word, count: w.count }))}
                      size={embed || isMobile ? 'compact' : (fontScale > 1.2 ? 'projector' : 'screen')}
                      limit={embed ? 40 : 60}
                    />
                  ) : (
                    <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '20px' }}>
                      {question.wordcloud_mode === 'words' ? 'Пока нет одобренных слов' : 'Пока нет одобренных ответов'}
                    </p>
                  )}
                </div>
              )}

              {chartType === 'bar' && (
                <VerticalBarChart data={question.options} height={qChartHeight} allowMultiple={question.allow_multiple} fontScale={fontScale} />
              )}

              {(chartType === 'horizontal' || question.type === 'ranking') && (
                <HorizontalBars
                  options={question.options || []}
                  ranking={question.type === 'ranking'}
                  allowMultiple={question.allow_multiple}
                  compact={embed || isMobile}
                  stacked={isMobile && !embed}
                  fitHeight={embed ? fitChartHeight : null}
                />
              )}

              {chartType === 'pie' && (
                <PieWithLegend options={question.options} height={embed ? fitChartHeight : (compactCharts ? null : Math.min(qChartHeight, 520))} />
              )}

              {question.type === 'heatmap' && question.image_id && (
                <HeatmapView
                  image={question.image_id}
                  data={question.heatmap}
                  maxHeight={embed ? fitChartHeight - 40 : (compactCharts ? null : barHeight - 90)}
                  compact={embed}
                  fontScale={fontScale}
                />
              )}

              {/* Сетка на картинке: число голосов в каждой ячейке, чем больше — тем насыщеннее */}
              {question.type === 'grid' && question.image_id && (() => {
                const cells = [...(question.options || [])].sort((a, b) => (parseInt(a.text, 10) || 0) - (parseInt(b.text, 10) || 0));
                const maxVotes = Math.max(...cells.map((o) => o.count), 1);
                const rows = question.grid_rows || 3;
                const cols = question.grid_cols || 3;
                return (
                  <figure className="grid-heat" style={{ '--grid-heat-max': embed ? `${fitChartHeight}px` : `${compactCharts ? 9999 : barHeight}px` }}>
                    <div className="grid-heat__frame">
                      <img src={`/proxy/image/${question.image_id}`} alt="Картинка с сеткой" />
                      <div className="grid-heat__cells" style={{ gridTemplateRows: `repeat(${rows}, 1fr)`, gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                        {cells.map((opt, idx) => {
                          const intensity = opt.count / maxVotes;
                          const isTop = opt.count > 0 && opt.count === maxVotes;
                          return (
                            <div
                              key={opt.id}
                              className={`grid-heat__cell${isTop ? ' is-top' : ''}`}
                              style={{ '--heat': opt.count > 0 ? (0.08 + intensity * 0.42).toFixed(2) : 0 }}
                              title={`Ячейка ${idx + 1}: ${opt.count} (${opt.percentage.toFixed(1)}%)`}
                            >
                              <span className="grid-heat__num">{idx + 1}</span>
                              {opt.count > 0 && <span className="grid-heat__count">{opt.count}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {!embed && <figcaption>Число в ячейке — сколько раз её выбрали. Самые популярные ячейки выделены.</figcaption>}
                  </figure>
                );
              })()}
            </div>
            </div>
            {question.other_answers && (
              <OtherPhrases
                data={question.other_answers}
                label={(question.options || []).find((o) => o.is_other)?.text || 'Другое'}
                compact={embed}
              />
            )}
          </div>
          );
        })}
      </div>

      {/* Плавающий QR-код только для Web-голосования */}
      {!embed && (
      <div className="floating-qr-codes">
        <div className="qr-code-wrapper web-qr">
          <QRCodeSVG
            value={voteUrl}
            size={160}
            level="M"
            includeMargin={true}
          />
          <p className="qr-code-label">Голосование в Web</p>
          <p className="qr-code-hint">{window.location.hostname}</p>
        </div>
        {share?.enabled && share.qr && (
          <div className="qr-code-wrapper web-qr share-qr">
            <QRCodeSVG value={shareUrl('poll', stats.poll_public_id || pollId)} size={160} level="M" includeMargin={true} />
            <p className="qr-code-label">Получить презентацию</p>
          </div>
        )}
      </div>
      )}

      {stats.questions.length === 0 ? (
        <div className="no-responses">
          <p>В опросе пока нет вопросов. Организатору нужно добавить их в личном кабинете.</p>
        </div>
      ) : stats.total_responses === 0 && !embed && (
        <div className="no-responses">
          <p>Пока нет ответов. Ожидайте участников...</p>
        </div>
      )}

      {embed && (
        <div className="embed-footer">
          {showEmbedNav ? (
            <div className="embed-nav">
              <button
                type="button"
                onClick={() => setEmbedIndex(Math.max(currentIndex - 1, 0))}
                disabled={currentIndex === 0}
                aria-label="Предыдущий вопрос"
              >‹</button>
              <span>Вопрос {currentIndex + 1} из {questionCount}</span>
              <button
                type="button"
                onClick={() => setEmbedIndex(Math.min(currentIndex + 1, questionCount - 1))}
                disabled={currentIndex >= questionCount - 1}
                aria-label="Следующий вопрос"
              >›</button>
            </div>
          ) : <span />}
          {!stats.company_logo_path && (
            <a className="embed-powered" href="https://svodx.ru" target="_blank" rel="noopener noreferrer">
              Работает на <SvodxLogo className="embed-powered__logo" withMark={false} />
            </a>
          )}
        </div>
      )}
      {!embed && !stats.company_logo_path && (
        <footer className="results-footer">
          Сделано в Лаборатории цифровых технологий и инноваций ·{' '}
          <a href="https://t.me/Nik0ls" target="_blank" rel="noopener noreferrer">написать нам</a>
        </footer>
      )}
    </div>
  );
}

export default PollResults;

